import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Mail } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/Button";
import { getErrorMessage } from "@/services/apiClient";
import { requestPasswordReset } from "@/services/authService";

/**
 * Forgot password — step 1 for local (password) accounts.
 *
 * The backend always answers with the same generic message, so this screen must
 * never claim that an account does or does not exist. When SMTP is not
 * configured (local development) the API returns the reset link itself, which we
 * surface in a clearly-labelled development panel.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [devLink, setDevLink] = useState("");
  const [devNote, setDevNote] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSent(false);
    setDevLink("");
    setDevNote("");
    if (!email.trim()) {
      setError("Enter the email address for your account.");
      return;
    }
    setLoading(true);
    try {
      const res = await requestPasswordReset(email.trim());
      setMessage(res.message);
      setSent(true);
      if (res.dev_reset_link) {
        setDevLink(res.dev_reset_link);
        setDevNote(res.dev_note || "");
      }
    } catch (err) {
      setError(getErrorMessage(err, "Could not send the reset link. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-50 px-4 py-12 dark:bg-zinc-950">
      <div className="pointer-events-none absolute -right-32 -bottom-32 h-[520px] w-[520px] rounded-full bg-[var(--amos-brand)] opacity-20 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full bg-[var(--amos-brand)] opacity-15 blur-3xl" />

      <div className="relative w-full max-w-lg rounded-2xl border border-zinc-200/80 bg-white/95 p-8 shadow-xl backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-900/90">
        <div className="mb-6 flex items-center gap-3">
          <BrandLogo className="h-10 w-auto rounded-xl shadow-sm" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Reset your password
            </h1>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              We will email you a secure link
            </p>
          </div>
        </div>

        <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-300">
          Enter the email address you use for MarketIQ and we will send you a link to choose a new
          password. The link works once and expires shortly.
        </p>

        {error ? (
          <div
            data-testid="forgot-password-error"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {error}
          </div>
        ) : null}

        {sent ? (
          <div
            data-testid="forgot-password-sent"
            className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">Check your email</p>
                <p className="mt-0.5">{message}</p>
                {devLink ? (
                  <div className="mt-2 rounded border border-emerald-300/70 bg-white/70 px-2 py-2 dark:border-emerald-800 dark:bg-zinc-900/60">
                    <p className="font-semibold">
                      {devNote || "SMTP is not configured (development only)."}
                    </p>
                    <a
                      data-testid="forgot-password-dev-link"
                      href={devLink}
                      className="mt-1 block break-all font-mono text-[11px] text-[var(--amos-brand)] underline"
                    >
                      {devLink}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-3.5">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
              <input
                type="email"
                required
                autoFocus
                data-testid="forgot-password-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full rounded-lg border border-zinc-200 bg-zinc-50/50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[var(--amos-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--amos-brand)]/20 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-100"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            data-testid="forgot-password-submit"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send reset link
            {!loading ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-100"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
